import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { D3Node, D3Link, Artifact } from '../../types/artifact';
import { usePrimeArtifact } from '../../store/hooks/usePrimeArtifact';
import { ArtifactService } from '../../services/api/artifactService';
import { RelationshipService } from '../../services/api/relationshipService';
import './ArtifactGraph.css';

interface ArtifactGraphProps {
  artifactId: string;
  onNodeSelect?: (nodeId: string) => void;
  onRelationshipSelect?: (relationshipId: string) => void;
}

export const ArtifactGraph: React.FC<ArtifactGraphProps> = ({
  artifactId,
  onNodeSelect,
  onRelationshipSelect,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [nodes, setNodes] = useState<D3Node[]>([]);
  const [links, setLinks] = useState<D3Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchArtifacts } = usePrimeArtifact();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all artifacts
        await fetchArtifacts();

        // Fetch the central artifact
        const centralArtifact = await ArtifactService.getById(artifactId);

        if (!centralArtifact) {
          console.error('Central artifact not found:', artifactId);
          return;
        }

        // Fetch relationships
        const relationships = await RelationshipService.getByArtifactId(artifactId);

        // Create nodes and links
        const artifactMap = new Map<string, Artifact>();
        artifactMap.set(centralArtifact.id, {
          id: centralArtifact.id,
          name: centralArtifact.name,
          type: centralArtifact.artifact_type_id || 'Unknown',
          state: 'active',
          created_at: centralArtifact.created_at || new Date().toISOString(),
          updated_at:
            centralArtifact.updated_at || centralArtifact.created_at || new Date().toISOString(),
          artifact_type_id: centralArtifact.artifact_type_id || 'Unknown',
        });

        // Fetch related artifacts
        for (const rel of relationships) {
          const relatedArtifactId =
            rel.direction === 'incoming' ? rel.source_artifact_id : rel.target_artifact_id;

          if (!artifactMap.has(relatedArtifactId)) {
            const relatedArtifact = await ArtifactService.getById(relatedArtifactId);
            if (relatedArtifact) {
              artifactMap.set(relatedArtifactId, {
                id: relatedArtifact.id,
                name: relatedArtifact.name,
                type: relatedArtifact.artifact_type_id || 'Unknown',
                state: 'active',
                created_at: relatedArtifact.created_at || new Date().toISOString(),
                updated_at:
                  relatedArtifact.updated_at ||
                  relatedArtifact.created_at ||
                  new Date().toISOString(),
                artifact_type_id: relatedArtifact.artifact_type_id || 'Unknown',
              });
            }
          }
        }

        // Convert artifacts to D3Nodes
        const d3Nodes: D3Node[] = Array.from(artifactMap.values()).map((artifact) => ({
          id: artifact.id,
          name: artifact.name,
          type: artifact.type,
          state: artifact.state,
          x: 0,
          y: 0,
        }));

        // Convert relationships to D3Links
        const d3Links: D3Link[] = relationships.map((rel) => ({
          source: rel.source_artifact_id,
          target: rel.target_artifact_id,
          type: rel.relationship_type,
          direction: rel.direction,
        }));

        setNodes(d3Nodes);
        setLinks(d3Links);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [artifactId, fetchArtifacts]);

  useEffect(() => {
    if (!svgRef.current || loading || error || nodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    const width = 800;
    const height = 600;

    // Clear previous content
    svg.selectAll('*').remove();

    // Create the container group
    const g = svg.append('g');

    // Create the force simulation
    const simulation = d3
      .forceSimulation<D3Node>(nodes)
      .force(
        'link',
        d3
          .forceLink<D3Node, D3Link>(links)
          .id((d: D3Node) => d.id)
          .distance(100)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2));

    // Create the links
    const link = g
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#999')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', (_d: D3Link) => Math.sqrt(1))
      .attr('class', 'relationship-line')
      .attr('data-relationship-id', (d: D3Link) => {
        const rel = links.find(
          (l) => l.source === d.source && l.target === d.target && l.type === d.type
        );
        return rel ? (rel as unknown as { id: string }).id : '';
      })
      .on('click', (event: MouseEvent, d: D3Link) => {
        if (onRelationshipSelect) {
          const rel = links.find(
            (l) => l.source === d.source && l.target === d.target && l.type === d.type
          );
          if (rel) {
            onRelationshipSelect((rel as unknown as { id: string }).id);
          }
        }
      });

    // Create the nodes
    const node = g
      .append('g')
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('r', 5)
      .attr('fill', (d: D3Node) => (d.id === artifactId ? '#ff0000' : '#69b3a2'))
      .attr('class', 'artifact-node')
      .attr('data-artifact-id', (d: D3Node) => d.id)
      .on('click', (event: MouseEvent, d: D3Node) => {
        if (onNodeSelect) {
          onNodeSelect(d.id);
        }
      });

    // Add labels
    const label = g
      .append('g')
      .selectAll('text')
      .data(nodes)
      .join('text')
      .text((d: D3Node) => d.name)
      .attr('font-size', 12)
      .attr('dx', 15)
      .attr('dy', 4)
      .attr('class', 'artifact-label');

    // Update positions on each tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d: D3Link) => (d.source as D3Node).x!)
        .attr('y1', (d: D3Link) => (d.source as D3Node).y!)
        .attr('x2', (d: D3Link) => (d.target as D3Node).x!)
        .attr('y2', (d: D3Link) => (d.target as D3Node).y!);

      node.attr('cx', (d: D3Node) => d.x!).attr('cy', (d: D3Node) => d.y!);

      label.attr('x', (d: D3Node) => d.x!).attr('y', (d: D3Node) => d.y!);
    });

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, [nodes, links, loading, error, artifactId, onNodeSelect, onRelationshipSelect]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="artifact-graph">
      <svg ref={svgRef} width="800" height="600" style={{ border: '1px solid #ccc' }} />
    </div>
  );
};

export default ArtifactGraph;
